// ink-component
import React, { MouseEvent, ChangeEvent, ReactNode, InputHTMLAttributes } from 'react';
import { ThemeConsumer } from 'styled-components';

import NewInput, { NewInputProps } from '../NewInput/NewInput';

import CalendarShortcuts from './components/NewDatePickerShortcuts/NewDatePickerShortcuts';
import Calendar from './components/NewDatePickerCalendar/NewDatePickerCalendar';
import { CalendarProps } from '../_common/CoreDatePicker/Calendar/Calendar';
import { CalendarShortcutsProps } from '../_common/CoreDatePicker/Calendar/CalendarShortcuts/CalendarShortcuts';
import CoreDatePicker from '../_common/CoreDatePicker/CoreDatePicker';
import TableDatePicker from './components/NewDatePickerTable/NewDatePickerTable';
import { DateDisplayFormat, LOCALES, LocaleType } from '../../helpers/datetime';

interface ReturnValue {
  value: string;
  unformattedValue: string;
}

export interface NewDatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Externally controlled value */
  value: string;
  /** onChange callback, sends the event and the value */
  onChange: (e: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>, returnValue: ReturnValue) => void;
  /** Render prop with an input component */
  input?: (inputProps: NewInputProps) => ReactNode;
  /** Render prop with a calendar component */
  calendar?: (calendarProps: CalendarProps) => ReactNode;
  /** Modifier for invalid input */
  invalid?: boolean;
  /** Render prop with the shortcuts component */
  shortcuts?: (shortcutsProps: CalendarShortcutsProps) => ReactNode;
  /** The date's mask */
  mask?: 'date' | 'short-date';
  /** format date */
  format?: Extract<DateDisplayFormat, 'condensed' | 'monthCondensed'>;
  /** Accepts a string with locale or an object with {input: locale, output: locale}. Supported locales: 'en-US' and 'en-GB'. */
  locale?:
    | LocaleType
    | {
        input: LocaleType;
        output: LocaleType;
      };
  /** Alignment of the calendar */
  align?: 'right' | 'left';
  /** Size modifier */
  width?: 'large' | 'half' | 'quarter' | 'full';
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  portalContainer?: React.RefObject<HTMLDivElement>;
}

/**
 * @purpose Allows users to choose dates
 * @group NewDatePicker
 * @categories form
 * @aka datepicker
 * @HTMLType HTMLInputElement
 */
function NewDatePicker(props: NewDatePickerProps) {
  const { className, style, ...otherProps } = props;
  return (
    <ThemeConsumer>
      {cellTheme => {
        if (cellTheme && cellTheme.cellPreset === 'editable') {
          return <TableDatePicker {...otherProps} />;
        }
        return <CoreDatePicker {...otherProps} />;
      }}
    </ThemeConsumer>
  );
}

NewDatePicker.Calendar = Calendar;
NewDatePicker.Shortcuts = CalendarShortcuts;

export { NewCalendarProps } from './components/NewDatePickerCalendar/NewDatePickerCalendar';
export { NewCalendarShortcutsProps } from './components/NewDatePickerShortcuts/NewDatePickerShortcuts';

NewDatePicker.defaultProps = {
  locale: LOCALES.EN_US,
  input: props => <NewInput {...props} />,
  calendar: props => <NewDatePicker.Calendar {...props} />,
  shortcuts: undefined,
  invalid: false,
  align: 'left',
  width: 'datePicker',
};

NewDatePicker.displayName = 'NewDatePicker';

export default NewDatePicker;
